// THE WAKE POLL — the display half of a run this client did not start. An OPEN conversation subscribes to
// exactly ONE run: the one its own send spawned (`streamChat(runId)` in api-client.ts). A run the SERVER
// starts — an inbound email reply waking the SAME Session — carries a run id the open client never learns,
// so nothing streams and nothing re-renders. The answer is already on the record while the human sits
// watching a window that never moves (production: the owner asked the same question twice, because the reply
// he was waiting for had been answered 23 seconds earlier in the conversation he had open).
//
// It is ONLY a display gap: every server read path for a session is UNFILTERED by trigger, so closing and
// reopening the conversation already shows the run. This hook repeats that same read while the conversation
// sits open and IDLE, and folds a run the view is not showing through the SAME apply path `openConversation`
// uses — never a second, divergent one.
//
// It must NEVER: fabricate a turn (P-SELF — everything rendered comes from the server read); read while a
// send or a stream is in flight (that collision is how one turn renders twice or a reply is lost); apply one
// conversation's read to another (the worst failure available here — another human's words in the wrong
// window); or surface a failed read (a transient 500 must not cost the human a visible conversation).
//
// A session-level SSE stream is the better long-term answer — the server knows the instant the inbound run
// starts, and would remove both the latency floor and the idle reads. This is the CLIENT-ONLY change that
// ships inside an already-queued deploy: no route, no migration, no new server surface.

import { useEffect, useRef } from 'react';
import { writeResumePointer } from './chat-resume.ts';
import type { SurfaceApi } from './api-client.ts';
import type { ChatSessionTranscriptView, ChatTranscriptView } from './projections.ts';

/**
 * How often an OPEN, IDLE conversation re-reads itself. 5s is the balance the demo needs at both ends: a
 * reply the server has already answered surfaces within one interval, and a reply whose run is still working
 * is SUBSCRIBED by the fold — so 5s is the worst a human ever waits, not the cadence the answer arrives at.
 * Against that, one open dock costs 12 reads a minute on a single-tenant box — noise beside the agent run
 * that produced the reply — and only while a conversation is open with nothing else in flight.
 */
export const CHAT_WAKE_POLL_MS = 5_000;

/** Exactly the transport the poll may touch — one owner-scoped read, no write, no spawn. */
type ChatWakeApi = Pick<SurfaceApi, 'getChatSession'>;

/**
 * The controller stores the poll reads. A structural SUBSET of `useChat`'s `ChatStores` (so the controller
 * passes its bundle straight in) naming exactly what a poll touches: the four guards it must respect, the
 * transcript mirror it compares against, and the two setters the shared apply path writes.
 */
export interface ChatWakeStores {
  readonly sessionIdRef: React.MutableRefObject<string | null>;
  readonly mountedRef: React.MutableRefObject<boolean>;
  /** Bumped by every send AND every conversation switch — a read that started before it moved is void. */
  readonly sendGenRef: React.MutableRefObject<number>;
  /** Bumped by every stream open / interrupt — same supersession discipline as a stream reconcile (§ER #6). */
  readonly streamGenRef: React.MutableRefObject<number>;
  /** The CURRENT transcript (a ref mirror) — its run is the latest run the view is already showing. */
  readonly transcriptRef: React.MutableRefObject<ChatTranscriptView | null>;
  readonly setTranscript: (view: ChatTranscriptView) => void;
  readonly setPriorRuns: React.Dispatch<React.SetStateAction<readonly ChatTranscriptView[]>>;
  /** True while a send is in flight. */
  readonly sending: boolean;
}

export interface ChatWakeInput {
  /** The OPEN conversation's session id, or null when none is open — starts and stops the poll. */
  readonly sessionId: string | null;
  /** True while a live preview is open or still reconciling to its snapshot. */
  readonly streaming: boolean;
  /** Subscribe to a still-running run the fold noticed — the SAME `openStream` path openConversation uses. */
  readonly subscribeIfRunning: (runId: string) => void;
}

/** IDLE = nothing else owns the transcript. A send in flight or a live/reconciling stream DOES: a read
 *  folded in beside one is how the same turn renders twice, or a just-arrived reply is overwritten. */
function isIdle(stores: ChatWakeStores, input: ChatWakeInput): boolean {
  return !stores.sending && !input.streaming;
}

/**
 * May the dock attach a LIVE PREVIEW to this conversation's latest run? Only when the run is still working
 * AND the reader owns it. A `schedule`-origin conversation is readable by every authenticated human (owner
 * ruling 2026-08-02) but its runs are the PLATFORM's, and both per-run surfaces are still initiator-scoped:
 * the SSE subscribe refuses pre-frame (assertMayStreamRun) and the per-run transcript read the reconcile
 * falls back to answers NOT_FOUND. Attaching therefore buys nothing and costs a recorded refusal against the
 * reader plus a stream that can only fail. The durable session read already carries the routine's record,
 * and the idle re-read below carries it forward as the run settles. Widening the per-run scopes to match is
 * a separate decision, not one to take by having the client knock on a door it knows is shut.
 */
export function mayAttachPreview(
  view: ChatSessionTranscriptView,
  latest: ChatTranscriptView,
): boolean {
  return latest.state === 'running' && view.origin !== 'schedule';
}

/**
 * Fold ONE server session read into the view: every earlier run becomes prior history, the latest run
 * becomes the active transcript, and a still-running latest is subscribed. The SHARED apply path of
 * `openConversation` and the wake poll — one function, so a run the poll notices lands exactly the way a
 * reopened conversation does. Everything rendered comes from `view` (P-SELF); the CALLER owns the
 * mounted/session/generation re-checks that must pass before this is reached.
 */
export function applySessionRead(
  stores: ChatWakeStores,
  sessionId: string,
  view: ChatSessionTranscriptView,
  subscribeIfRunning: (runId: string) => void,
): void {
  if (view.runs.length === 0) return; // an honest empty conversation — never fabricated turns
  const latest = view.runs[view.runs.length - 1]!;
  stores.setPriorRuns(view.runs.slice(0, -1));
  stores.setTranscript(latest);
  writeResumePointer({ sessionId, runId: view.latestRunId });
  // If the latest run is STILL RUNNING *and this reader owns it*, subscribe so the preview + activity
  // stream in and the answer reconciles on end — otherwise the dock would show "working…" forever with no
  // subscription (review fix). The broker replays already-released activity/deltas to this late joiner,
  // then emits `end`. A routine's run is deliberately NOT subscribed — see mayAttachPreview.
  if (mayAttachPreview(view, latest)) subscribeIfRunning(latest.runId);
}

/**
 * ONE tick. Reads the open conversation and folds in a run the view is not showing. Every guard is a
 * re-check of the world after an await, in the controller's own discipline: mounted, same conversation, and
 * neither generation moved (a send or a stream that started under this read supersedes it — §ER #6/#10).
 */
async function wakePoll(
  api: ChatWakeApi,
  sessionId: string,
  storesRef: React.MutableRefObject<ChatWakeStores>,
  inputRef: React.MutableRefObject<ChatWakeInput>,
  stopped: () => boolean,
): Promise<void> {
  const stores = storesRef.current;
  if (!isIdle(stores, inputRef.current) || !stores.mountedRef.current) return;
  if (stores.sessionIdRef.current !== sessionId) return;
  const sendGeneration = stores.sendGenRef.current;
  const streamGeneration = stores.streamGenRef.current;
  let view: ChatSessionTranscriptView;
  try {
    view = await api.getChatSession(sessionId);
  } catch {
    // SILENT: a transient read failure leaves the visible conversation exactly as it is, shows no error,
    // and stops nothing — the next tick tries again. Losing a rendered conversation to one failed read
    // would be worse than the gap this hook closes.
    return;
  }
  const after = storesRef.current;
  if (stopped() || !after.mountedRef.current) return;
  // The conversation moved under the read: a switch, a New conversation, or a send that started while the
  // read was in flight. Applying now would put THIS conversation's turns into the one now on screen.
  if (after.sessionIdRef.current !== sessionId) return;
  if (after.sendGenRef.current !== sendGeneration) return;
  if (after.streamGenRef.current !== streamGeneration) return;
  if (!isIdle(after, inputRef.current)) return;
  // Compare against the read's OWN last run rather than its `latestRunId` label: after the fold that run
  // IS the transcript, so the next tick is a no-op — the poll cannot re-fold (or re-subscribe) in a loop.
  // The run's STATE is part of that comparison, not just its id: a run the view is already showing as
  // `running` is the one case where re-reading is the whole point (a routine's occurrence is never
  // subscribed, so the settled answer arrives ONLY on a later read). Once folded, the shown state equals
  // the read state again and the next tick no-ops — still no loop, and still no repeat subscribe, because
  // a run that changed state is by definition no longer running.
  const latest = view.runs[view.runs.length - 1];
  const shown = after.transcriptRef.current;
  if (latest === undefined) return;
  if (latest.runId === shown?.runId && latest.state === shown.state) return;
  applySessionRead(after, sessionId, view, inputRef.current.subscribeIfRunning);
}

/**
 * Re-read the OPEN conversation on an interval while it is idle, so a run started server-side becomes
 * visible without the human closing and reopening it. Mounted only while the dock is; keyed ONLY by the open
 * conversation, so a switch stops the old poll and starts the new one, and nothing else restarts the timer.
 */
export function useChatWake(api: ChatWakeApi, stores: ChatWakeStores, input: ChatWakeInput): void {
  // Both bundles are rebuilt every render (they carry `sending` and the stream flag). Route through refs:
  // an effect that depended on them would tear down and re-create the interval on every render — including
  // every keystroke in the composer — and the poll would never live long enough to fire.
  const storesRef = useRef(stores);
  storesRef.current = stores;
  const inputRef = useRef(input);
  inputRef.current = input;
  const { sessionId } = input;
  useEffect(() => {
    let stopped = false;
    // No conversation on the server yet (before the first send, or after New conversation) — nothing to
    // re-read, and no id to read it under.
    const timer =
      sessionId === null
        ? null
        : setInterval(() => {
            void wakePoll(api, sessionId, storesRef, inputRef, () => stopped);
          }, CHAT_WAKE_POLL_MS);
    return (): void => {
      // A read already in flight applies nothing after this: the conversation changed or the dock unmounted.
      stopped = true;
      if (timer !== null) clearInterval(timer);
    };
  }, [api, sessionId]);
}
